import * as Card from './ScanResultComponent.styles';
import * as Styled from './ManualCheckIn.styles';

import {
	Attendee,
	AttendeeTicket,
	CheckInResponse,
	checkInTickets,
	getAttendeeTickets,
	searchAttendees,
	undoCheckIn,
} from './services';
import { Check, ChevronLeft } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { canUndoCheckIn } from 'utils/utils';
import { haptic } from './haptics';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const ALREADY_SCANNED = 'already validated';
const TRANSFERRED_AWAY = 'sent_transfer_accepted';

type TicketState =
	| { kind: 'checking' }
	| { kind: 'ready' }
	| { kind: 'in' }
	| { kind: 'blocked'; label: string; reason: string };

/*
 * The ticket list's own `validated` flag is read against the event in the URL, so on day 2 of a
 * scanner-grouped festival a pass would still show day 1. The verdict comes from a dry-run validate,
 * which judges each ticket against the same show a real check-in would hit.
 */
const ticketState = (ticket: AttendeeTicket, verdict?: CheckInResponse, verdictFailed?: boolean): TicketState => {
	if (ticket.status === TRANSFERRED_AWAY) {
		return { kind: 'blocked', label: 'Transferred', reason: `Transferred to ${ticket.recipient || 'someone else'}` };
	}
	if (!verdict) {
		if (!verdictFailed) return { kind: 'checking' };
		return ticket.validated ? { kind: 'in' } : { kind: 'ready' };
	}
	const reason = (verdict.errors || []).find((e) => Number(e.ticket_id) === ticket.id)?.error?.Error;
	if (!reason) return { kind: 'ready' };
	if (reason.includes(ALREADY_SCANNED)) return { kind: 'in' };
	return { kind: 'blocked', label: 'Cannot check in', reason };
};

const requestError = (error: unknown, fallback: string) => {
	const data = (error as { response?: { data?: unknown } })?.response?.data;
	if (typeof data === 'string' && data) return data;
	const message = (data as { error?: string } | undefined)?.error;
	return message || fallback;
};

const STATUS_TEXT: Record<TicketState['kind'], string> = {
	checking: 'Checking…',
	ready: 'Not checked in',
	in: 'Checked in',
	blocked: '',
};

const AttendeeTickets: FC<{ eventId: number; attendee: Attendee; onBack: () => void }> = ({
	eventId,
	attendee,
	onBack,
}) => {
	const [inlineErrors, setInlineErrors] = useState<Record<number, string>>({});
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [undoArmed, setUndoArmed] = useState(false);
	const canUndo = canUndoCheckIn(localStorage.getItem('accessToken'));

	const tickets = useQuery(
		['manual-checkin-tickets', eventId, attendee.user_id],
		() => getAttendeeTickets(eventId, attendee.user_id),
		{ retry: false, refetchOnWindowFocus: false }
	);
	const ticketIds = (tickets.data || []).map((ticket) => ticket.id);

	const verdict = useQuery(
		['manual-checkin-verdict', eventId, ticketIds],
		() => checkInTickets({ ticketIds, eventId, dryRun: true }),
		{ enabled: ticketIds.length > 0, retry: false, refetchOnWindowFocus: false }
	);

	const rows = (tickets.data || []).map((ticket) => ({
		ticket,
		state: ticketState(ticket, verdict.data, verdict.isError),
	}));
	const selectable = (state: TicketState) => state.kind === 'ready' || (state.kind === 'in' && canUndo);
	const selectableIds = rows.filter((row) => selectable(row.state)).map((row) => row.ticket.id);
	const selectedRows = rows.filter((row) => selected.has(row.ticket.id) && selectable(row.state));
	const toCheckIn = selectedRows.filter((row) => row.state.kind === 'ready').map((row) => row.ticket.id);
	const toUndo = selectedRows.filter((row) => row.state.kind === 'in').map((row) => row.ticket.id);

	useEffect(() => {
		if (!undoArmed) return;
		const timer = setTimeout(() => setUndoArmed(false), 3000);
		return () => clearTimeout(timer);
	}, [undoArmed]);

	const settle = (failures: Record<number, string>) => {
		setInlineErrors(failures);
		setSelected(new Set(Object.keys(failures).map(Number)));
		haptic(Object.keys(failures).length > 0 ? 'error' : 'success');
		tickets.refetch();
		verdict.refetch();
	};

	const { mutate: checkIn, isLoading: checkingIn } = useMutation(
		(ids: number[]) => checkInTickets({ ticketIds: ids, eventId }),
		{
			onSuccess: (data) => {
				const failures: Record<number, string> = {};
				(data?.errors || []).forEach((e) => {
					failures[Number(e.ticket_id)] = e.error?.Error || 'Check-in refused';
				});
				settle(failures);
			},
			onError: (error, ids) => {
				const message = requestError(error, 'Check-in failed. Try again.');
				settle(Object.fromEntries(ids.map((id) => [id, message])));
			},
		}
	);

	const { mutate: unscan, isLoading: unscanning } = useMutation(
		async (ids: number[]) => {
			const failures: Record<number, string> = {};
			for (const ticketId of ids) {
				try {
					await undoCheckIn({ ticketId, eventID: eventId });
				} catch (error) {
					failures[ticketId] = requestError(error, 'Could not un-scan this ticket');
				}
			}
			return failures;
		},
		{ onSuccess: settle, onSettled: () => setUndoArmed(false) }
	);

	const busy = checkingIn || unscanning;

	const toggle = (ticketId: number) => {
		haptic('tap');
		setUndoArmed(false);
		setSelected((current) => {
			const next = new Set(current);
			if (next.has(ticketId)) next.delete(ticketId);
			else next.add(ticketId);
			return next;
		});
	};

	const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
	const toggleAll = () => {
		haptic('tap');
		setUndoArmed(false);
		setSelected(allSelected ? new Set() : new Set(selectableIds));
	};

	const onUnscan = () => {
		if (!undoArmed) {
			haptic('warning');
			setUndoArmed(true);
			return;
		}
		unscan(toUndo);
	};

	return (
		<Styled.Container $withBar={selectedRows.length > 0}>
			<Styled.Person>
				<Styled.BackButton type="button" onClick={onBack} aria-label="Back to search">
					<ChevronLeft size={26} strokeWidth={2.25} aria-hidden />
				</Styled.BackButton>
				<Styled.PersonText>
					<Styled.Name>{attendee.full_name || 'No name'}</Styled.Name>
					<Styled.Email>{attendee.email}</Styled.Email>
				</Styled.PersonText>
			</Styled.Person>

			{tickets.isLoading && <Styled.Hint>Loading tickets…</Styled.Hint>}
			{tickets.isError && <Styled.Hint $error>Could not load tickets. Go back and try again.</Styled.Hint>}
			{tickets.data && tickets.data.length === 0 && <Styled.Hint>No tickets for this event.</Styled.Hint>}

			{rows.length > 0 && (
				<Styled.ListHeader>
					<span>
						{rows.length} {rows.length === 1 ? 'ticket' : 'tickets'}
					</span>
					{selectableIds.length > 1 && (
						<Styled.TextButton type="button" onClick={toggleAll} disabled={busy}>
							{allSelected ? 'Deselect all' : 'Select all'}
						</Styled.TextButton>
					)}
				</Styled.ListHeader>
			)}

			{rows.length > 0 && (
				<Styled.TicketList>
					{rows.map(({ ticket, state }) => {
						const canSelect = selectable(state);
						const isSelected = canSelect && selected.has(ticket.id);
						const error = inlineErrors[ticket.id] || (state.kind === 'blocked' ? state.reason : '');
						return (
							<li key={ticket.id}>
								<Styled.TicketRow
									type="button"
									role="checkbox"
									aria-checked={isSelected}
									aria-disabled={!canSelect || busy}
									data-testid={`ticket-${ticket.id}`}
									$color={ticket.ticket_type_color || '#3f3f46'}
									$selected={isSelected}
									onClick={() => canSelect && !busy && toggle(ticket.id)}
								>
									<Styled.TicketText>
										<Styled.TicketName>{ticket.ticket_type_name || 'Ticket'}</Styled.TicketName>
										{ticket.seat_id && <Styled.TicketSub>Seat {ticket.seat_id}</Styled.TicketSub>}
										<Styled.TicketStatus $tone={state.kind}>
											{state.kind === 'blocked' ? state.label : STATUS_TEXT[state.kind]}
											<Styled.TicketId> · #{ticket.id}</Styled.TicketId>
										</Styled.TicketStatus>
										{error && <Styled.InlineError role="alert">{error}</Styled.InlineError>}
									</Styled.TicketText>
									{state.kind === 'in' && !isSelected && (
										<Styled.CheckedIn aria-hidden>
											<Check size={16} strokeWidth={3} />
										</Styled.CheckedIn>
									)}
									{canSelect && (state.kind !== 'in' || isSelected) && (
										<Styled.Box $checked={isSelected} $undo={state.kind === 'in'} aria-hidden>
											{isSelected && <Check size={16} strokeWidth={3} />}
										</Styled.Box>
									)}
								</Styled.TicketRow>
							</li>
						);
					})}
				</Styled.TicketList>
			)}

			{selectedRows.length > 0 && (
				<Styled.ActionBar>
					{toCheckIn.length > 0 && (
						<Card.PrimaryAction type="button" disabled={busy} onClick={() => checkIn(toCheckIn)}>
							{checkingIn ? 'Checking in…' : `Check in ${toCheckIn.length}`}
						</Card.PrimaryAction>
					)}
					{toUndo.length > 0 && (
						<Card.SecondaryAction type="button" disabled={busy} $armed={undoArmed} onClick={onUnscan}>
							{unscanning
								? 'Un-scanning…'
								: undoArmed
								? `Tap again to un-scan ${toUndo.length}`
								: `Un-scan ${toUndo.length}`}
						</Card.SecondaryAction>
					)}
					<Styled.TextButton type="button" disabled={busy} onClick={() => setSelected(new Set())}>
						Clear selection
					</Styled.TextButton>
				</Styled.ActionBar>
			)}
		</Styled.Container>
	);
};

export const ManualCheckIn: FC<{ eventId: number }> = ({ eventId }) => {
	const [input, setInput] = useState('');
	const [term, setTerm] = useState('');
	const [selected, setSelected] = useState<Attendee | null>(null);

	useEffect(() => {
		const timer = setTimeout(() => setTerm(input.trim()), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [input]);

	const searching = term.length >= MIN_CHARS;
	const results = useQuery(['manual-checkin-search', eventId, term], () => searchAttendees(eventId, term), {
		enabled: searching,
		retry: false,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	});

	if (selected) {
		return <AttendeeTickets eventId={eventId} attendee={selected} onBack={() => setSelected(null)} />;
	}

	const attendees = searching ? results.data || [] : [];

	return (
		<Styled.Container>
			<Styled.SearchInput
				type="search"
				inputMode="search"
				autoComplete="off"
				autoCorrect="off"
				spellCheck={false}
				placeholder="Name, email, ticket # or order #"
				aria-label="Search attendees"
				value={input}
				onChange={(e) => setInput(e.target.value)}
			/>

			{!searching && <Styled.Hint>Type at least {MIN_CHARS} characters.</Styled.Hint>}
			{searching && results.isFetching && attendees.length === 0 && <Styled.Hint>Searching…</Styled.Hint>}
			{searching && results.isError && (
				<Styled.Hint $error>Search failed. Check the connection and try again.</Styled.Hint>
			)}
			{searching && results.isSuccess && !results.isFetching && attendees.length === 0 && (
				<Styled.Hint>{`No attendees match "${term}".`}</Styled.Hint>
			)}

			{attendees.length > 0 && (
				<Styled.Results>
					{attendees.map((attendee) => (
						<li key={attendee.user_id}>
							<Styled.ResultButton
								type="button"
								onClick={() => {
									haptic('tap');
									setSelected(attendee);
								}}
							>
								<Styled.Name>{attendee.full_name || 'No name'}</Styled.Name>
								<Styled.Email>{attendee.email}</Styled.Email>
							</Styled.ResultButton>
						</li>
					))}
				</Styled.Results>
			)}
		</Styled.Container>
	);
};
